import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { isAuthenticated } from "services/Auth";
import { apiMain } from "services";
import { setServerResponse, showAuthModal } from "store/application/application-action";
import { useDispatch, useSelector } from "react-redux";

import { routes } from "routes";
import { IGif, IUser } from "services/types";
import { IAppState } from "store/app-state";
import VerifiedUserIcon from "../UI/VerifiedUserIcon";
import plusIcon from "components/icons/plusIcon.svg";
import checkIcon from "components/icons/tick.svg";
import { useFollowingIdsQuery } from "queries";
import DEFAULT_USERPIC from "components/icons/default_userpic.svg";

import "./UserInfo.scss";

const TIMESTAMP_TO_TIME = 1000;

export const UserInfo = (props: {
  gif: IGif;
  user: IUser;
  external: boolean;
  rel?: string;
}) => {
  const { user, gif } = props;
  const currentUser = useSelector((state: IAppState) => state.user.currentUser);
  const [showMore, setShowMore] = useState(false);
  const [following, setFollowing] = useState(false);
  const dispatch = useDispatch();
  const followingIds = useFollowingIdsQuery();
  const userpic = props.user.profileImageUrl || DEFAULT_USERPIC;
  const isCurrentUserGif = currentUser?.username === user.username;
  const createDate = new Date(gif.createDate * TIMESTAMP_TO_TIME).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });

  useEffect(() => {
    if (followingIds.status === "success" && followingIds.data) {
      const isFollowing = followingIds.data.indexOf(user.username) >= 0;
      setFollowing(isFollowing);
    }
  }, [followingIds.status, followingIds.data, user.username]);

  const handleFollow = () => {
    isAuthenticated().then((isLoggedIn) => {
      if (isLoggedIn) {
        apiMain.followUser(user.username).then((res) => {
          setFollowing(true);
          dispatch(setServerResponse(`Started following ${user.username}.`));
        }).catch(() => {
          setFollowing(false);
          dispatch(setServerResponse(`Could not follow ${user.username}`));
        });
      } else {
        dispatch(showAuthModal());
      }
    });
  };

  const renderDescription = () => {
    const gifDescription = gif.description || user.description;

    if (!gifDescription) {
      return null;
    }

    const MAX_LENGTH = 40;
    const isShowMore = gifDescription?.length < MAX_LENGTH;

    return (
      <p className="author-description">
        {isShowMore || showMore ? gifDescription : `${gifDescription?.substring(0, MAX_LENGTH)}...`}
        {" "}
        {!isShowMore && <button onClick={() => setShowMore(prev => !prev)}>{showMore ? "Less" : " More"}</button>}
      </p>
    );
  };

  return (
    <>
      <div className="author">
        <div className="userpic">
          <Link
            to={routes.user(user.username)}
            target={props.external ? "_blank" : undefined}
            rel="noopener noreferrer"
          >
            <img className="userpic" src={userpic} alt=""/>
          </Link>
        </div>
        {!isCurrentUserGif && (
          <button className="follow-btn" title={user.username} onClick={handleFollow} disabled={following}>
            <img src={following ? checkIcon : plusIcon} alt="follow icon" />
          </button>
        )}
        <div className="usertext">
          <div className="date">
            <Link
              to={routes.watch(gif.id, props.rel)}
              target={props.external ? "_blank" : undefined}
              rel="noopener noreferrer"
            >{createDate}</Link>
          </div>
          <div className="link">
            <Link
              to={routes.user(user.username)}
              target={props.external ? "_blank" : undefined}
              rel="noopener noreferrer"
            >
              {user.name || user.username}
              {user.verified && <VerifiedUserIcon />}
            </Link>
          </div>
        </div>
      </div>
      {renderDescription()}
    </>
  );
};
